import React, { useEffect, useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";
import api from "@/lib/api";
import ProductCard from "@/components/ProductCard";
import { SlidersHorizontal, X } from "lucide-react";

export default function ProductList() {
  const [params, setParams] = useSearchParams();
  const [products, setProducts] = useState([]);
  const [cats, setCats] = useState([]);
  const [brands, setBrands] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filtersOpen, setFiltersOpen] = useState(false);

  const filters = useMemo(() => ({
    q: params.get("q") || "",
    category: params.get("category") || "",
    brand: params.get("brand") || "",
    trending: params.get("trending"),
    bestseller: params.get("bestseller"),
    new: params.get("new"),
    min_price: params.get("min_price"),
    max_price: params.get("max_price"),
  }), [params]);

  useEffect(() => { api.get("/categories").then(r => setCats(r.data)); api.get("/brands").then(r => setBrands(r.data)); }, []);

  useEffect(() => {
    setLoading(true);
    const qs = new URLSearchParams();
    Object.entries(filters).forEach(([k, v]) => { if (v) qs.set(k, v); });
    api.get(`/products?${qs.toString()}`).then(r => setProducts(r.data)).finally(() => setLoading(false));
  }, [filters]);

  const update = (k, v) => {
    const next = new URLSearchParams(params);
    if (!v) next.delete(k); else next.set(k, v);
    setParams(next);
  };

  return (
    <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
      <div className="flex items-end justify-between mb-6">
        <div>
          <h1 className="font-display text-3xl md:text-4xl font-bold">{filters.q ? `Results for "${filters.q}"` : filters.category || "All Products"}</h1>
          <p className="text-sm text-slate-500 mt-1">{products.length} products</p>
        </div>
        <button data-testid="filters-toggle" onClick={() => setFiltersOpen(v => !v)} className="lg:hidden inline-flex items-center gap-2 px-4 py-2 rounded-full bg-slate-100">
          <SlidersHorizontal size={16}/> Filters
        </button>
      </div>

      <div className="grid lg:grid-cols-[260px_1fr] gap-8">
        <aside className={`${filtersOpen ? "block" : "hidden lg:block"} space-y-6`}>
          <FilterGroup title="Category">
            <FilterPill active={!filters.category} onClick={() => update("category", "")}>All</FilterPill>
            {cats.map(c => (
              <FilterPill key={c.category_id} active={filters.category === c.name} onClick={() => update("category", c.name)}>{c.name}</FilterPill>
            ))}
          </FilterGroup>
          <FilterGroup title="Brand">
            <FilterPill active={!filters.brand} onClick={() => update("brand", "")}>All</FilterPill>
            {brands.map(b => (
              <FilterPill key={b.brand_id} active={filters.brand === b.name} onClick={() => update("brand", b.name)}>{b.name}</FilterPill>
            ))}
          </FilterGroup>
          <FilterGroup title="Price">
            <div className="flex items-center gap-2">
              <input data-testid="filter-minprice" type="number" placeholder="Min" defaultValue={filters.min_price||""} onBlur={(e)=>update("min_price",e.target.value)} className="w-full bg-slate-100 px-3 py-2 rounded-lg text-sm" />
              <input data-testid="filter-maxprice" type="number" placeholder="Max" defaultValue={filters.max_price||""} onBlur={(e)=>update("max_price",e.target.value)} className="w-full bg-slate-100 px-3 py-2 rounded-lg text-sm" />
            </div>
          </FilterGroup>
          {(filters.category || filters.brand || filters.q || filters.min_price || filters.max_price) && (
            <button data-testid="clear-filters" onClick={() => setParams({})} className="text-sm text-[#FF4500] inline-flex items-center gap-1"><X size={14}/>Clear all</button>
          )}
        </aside>
        <section>
          {loading ? (
            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
              {Array.from({length:8}).map((_,i) => <div key={i} className="aspect-[4/5] bg-slate-100 rounded-2xl animate-pulse" />)}
            </div>
          ) : products.length === 0 ? (
            <div className="text-center py-24">
              <div className="font-display text-2xl font-semibold mb-2">No products found</div>
              <p className="text-slate-500">Try adjusting your filters.</p>
            </div>
          ) : (
            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4 md:gap-6">
              {products.map(p => <ProductCard key={p.product_id} p={p} />)}
            </div>
          )}
        </section>
      </div>
    </main>
  );
}

function FilterGroup({ title, children }) {
  return <div><div className="text-xs font-bold uppercase tracking-widest text-slate-500 mb-3">{title}</div><div className="flex flex-wrap gap-2">{children}</div></div>;
}
function FilterPill({ active, onClick, children }) {
  return <button onClick={onClick} className={`px-3 py-1.5 rounded-full text-xs font-medium border transition ${active ? "bg-[#004BCA] text-white border-[#004BCA]" : "bg-white border-slate-200 text-slate-700 hover:border-slate-400"}`}>{children}</button>;
}
